import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { useBodyScrollDisable, useOutsideClick } from '@/hooks'
import { close } from '@/store/modalsSlice'
import { device } from '@/common/constants/device'
import classNames from 'classnames'
import useIsVisible from '@/hooks/useIsVisible'
import { CreativesCustomScrollbar } from '@/components/Creatives/CreativesStyled'

const ModalContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  position: relative;
  max-height: 85vh;
  height: 100%;
  overflow: hidden;
  max-width: ${(props) => (props.maxWidth ? `${props.maxWidth}px` : '100%')};
  width: ${(props) => (props.maxWidth ? '100%' : 'auto')};
`

const ModalInnerContent = styled.div`
  max-height: 100%;
  position: relative;
  width: 100%;
  background-color: var(--white);
  border-radius: 12px;
  box-shadow: 2px 2px 4px 2px rgba(26, 32, 44, 0.16);
`

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding-top: ${({ needOffsetHeader }) => (needOffsetHeader ? `79px` : 0)};
  padding-bottom: ${({ needOffsetHeader }) => (needOffsetHeader ? `15px` : 0)};
  z-index: 999;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;

  @media ${device.tablet} {
    padding-top: 96px;
    padding-bottom: 0;
  }

  &.full-overlay {
    z-index: 999;

    @media ${device.tablet} {
      padding-top: 0;
    }
  }

  &.scrolled {
    .modal-inner-content {
      overflow-y: auto;
      ${CreativesCustomScrollbar};
    }
  }
`

const Modal = ({
  children,
  maxWidth,
  isGoBack,
  isScrolled = true,
  isFullOverlay = true,
  className = '',
  isClickOutside,
  onClose = () => {},
  ...props
}) => {
  const dispatch = useDispatch()
  const modalWrapperRef = useRef(null)
  const wrapperRef = useRef(null)
  const router = useRouter()
  const modalClasses = classNames({
    scrolled: isScrolled,
    'full-overlay': isFullOverlay,
  })

  useEffect(() => {
    const onEscapeClick = (e) => {
      if (e.keyCode === 27) {
        closeModal()
      }
      if (e.keyCode === 27 && isGoBack) {
        router.back()
      }
    }
    window.addEventListener('keydown', onEscapeClick)

    return () => window.removeEventListener('keydown', onEscapeClick)
  }, [])

  const isVisible = useIsVisible(modalWrapperRef)

  const closeModal = () => {
    onClose()
    dispatch(close())
  }

  useBodyScrollDisable()

  useOutsideClick(() => {
    if (!isVisible || isClickOutside) return
    if (isGoBack) {
      router.back()
    }
    closeModal()
  }, wrapperRef)

  return (
    <ModalWrapper
      className={`${className} ${modalClasses}`}
      ref={modalWrapperRef}
      {...props}
    >
      <ModalContent className="modal-content" maxWidth={maxWidth} id="modal">
        <ModalInnerContent ref={wrapperRef} className="modal-inner-content">
          {children}
        </ModalInnerContent>
      </ModalContent>
    </ModalWrapper>
  )
}

export default Modal
